import React, { useState, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Box, Button, InputFormField, Text } from '@indeed/ifl-components';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import {
    useGetNameFormDataQuery,
    useUpdateLightApplyNameInfoMutation,
} from './__generated__/NameInputPage.generated';
import {
    SmartApplyContactInfoUpdate,
    updateSmartApplyContactInfoCache,
    buildCompleteContactInfoMutationInput,
    validateCacheServerConsistency,
} from '../../../lib/apollo/smartApplyStateCache';
import PageTransition from '../../../components/PageTransition/PageTransition';
import FormHeader from '../components/FormHeader/FormHeader';
import { NodeIdContext } from '../../../lib/context/NodeIdContext';
import { useSearchParams } from 'react-router-dom';

/**
 * NameInputPage with Direct SmartApplyApplicationState Cache Updates
 *
 * This approach:
 * 1. ✅ Writes directly to SmartApplyApplicationState cache (no separate local state)
 * 2. ✅ Partial cache writes on field changes (immediate UI updates)
 * 3. ✅ Single server mutation on "Continue" (bulk save)
 * 4. ✅ Uses OneGraph schema as the single source of truth
 */
const NameInputPage: React.FC = () => {
    const { goForward, loading: pageFlowLoading } = usePageFlow();
    const apolloClient = useApolloClient();

    const [searchParams] = useSearchParams();
    const indeedApplyableJobId = searchParams.get('indeedApplyableJobId') || '';
    // Get nodeId from context
    const nodeId = useContext(NodeIdContext);
    // const nodeId = 'apply_state_123';

    // 1. Get server data (SmartApply + JobSeeker profile + ApplyParameters name config) - Apollo should auto-update on cache changes
    const { data, loading } = useGetNameFormDataQuery({
        variables: {
            nodeId: nodeId || '', // Provide empty string as fallback for TypeScript
            indeedApplyableJobId, // Pass job ID to get name configuration from applyParameters
        },
        skip: !nodeId || !indeedApplyableJobId, // Skip query when either parameter is missing
        errorPolicy: 'all',
        // Cache-first: return cache data immediately, only fetch if not in cache
        fetchPolicy: 'cache-first',
    });

    // 2. Server mutation for bulk save on Continue
    const [updateServerNameInfo, { loading: saving }] = useUpdateLightApplyNameInfoMutation();

    // 3. Extract current state from Apollo data - should auto-update when data changes
    const smartApplyState =
        data?.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ? data.smartApplyApplicationState
            : null;

    const contactInfo = smartApplyState?.contactInfo;

    // 4. Extract name field configuration from applyParameters
    const nameConfig = data?.applyParameters?.jobDetails?.standardFields;
    // In light-apply, we always use FIRST_LAST_NAME format
    // const nameFormat = nameConfig?.name?.format; // 'FIRST_LAST_NAME' or 'FULL_NAME'
    const namePronunciationRequirement = nameConfig?.resumeFields?.namePronunciation?.requirement;

    // TODO: Use name configuration to:
    // - Validate required fields based on configuration

    // 5. Handle form field changes with direct cache writes - Apollo should auto-update
    const handleFieldChange = (field: keyof SmartApplyContactInfoUpdate, value: string) => {
        // Prevent cache updates while data is still loading
        if (loading) {
            return;
        }

        if (!smartApplyState?.id) {
            console.warn('No SmartApplyApplicationState ID available for cache update');
            return;
        }

        // Use cache.modify() - more flexible, no fragment validation issues
        const updatedContactInfo = updateSmartApplyContactInfoCache(
            apolloClient.cache,
            smartApplyState.id,
            { [field]: value },
        );

        if (!updatedContactInfo) {
            console.error(`Failed to update ${field}`);
        }
    };

    // State to track when user has attempted to continue (for validation UX)
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

    // 5. Handle Continue - ✅ SAFE: Build complete mutation to prevent data loss
    const handleContinue = async () => {
        if (!smartApplyState?.id || !contactInfo) {
            console.error('Cannot save: missing SmartApplyApplicationState ID or contact info');
            return;
        }

        // TODO: IAJPE-185 - Improve form validation to be more dynamic and user-friendly
        // - Use schema-based validation with proper error messages
        // - Show field-specific validation on blur/change instead of only on submit
        // - Implement real-time validation feedback
        // - Consider name pronunciation requirement configuration from server

        // Validation - require name fields based on configuration
        const isPronunciationRequired = namePronunciationRequirement === 'REQUIRED';
        const hasRequiredFields = !!(
            contactInfo.firstName?.trim() &&
            contactInfo.lastName?.trim() &&
            (!isPronunciationRequired || contactInfo.firstNamePronunciation?.trim()) &&
            (!isPronunciationRequired || contactInfo.lastNamePronunciation?.trim())
        );

        if (!hasRequiredFields) {
            // Show validation errors now that user has attempted to continue
            setHasAttemptedContinue(true);
            return;
        }

        // ✅ CRITICAL: Build complete mutation input to prevent server data loss
        // The server does NOT support partial updates - must include ALL contactInfo fields
        const completeMutationInput = buildCompleteContactInfoMutationInput(
            contactInfo, // Complete current contactInfo from cache
            {
                // Only the fields we want to update (but all existing fields preserved)
                firstName: contactInfo.firstName,
                lastName: contactInfo.lastName,
                ...(isPronunciationRequired
                    ? {
                          firstNamePronunciation: contactInfo.firstNamePronunciation,
                          lastNamePronunciation: contactInfo.lastNamePronunciation,
                      }
                    : {}),
            },
        );

        const mutationVariables = {
            input: { id: smartApplyState.id },
            updateContactInfoInput: completeMutationInput,
        };

        try {
            // Send complete contactInfo to server (all fields included)
            await updateServerNameInfo({
                variables: mutationVariables,
                // ✅ Validate that server preserved all data
                update: (cache, { data: mutationData }) => {
                    if (mutationData?.smartApplyApplicationState?.updateContactInfo?.state) {
                        const serverContactInfo =
                            mutationData.smartApplyApplicationState.updateContactInfo.state;

                        // ✅ Validate that server returned ALL fields we sent (data preservation check)
                        validateCacheServerConsistency(
                            completeMutationInput,
                            serverContactInfo,
                            'UpdateLightApplyNameInfo',
                        );

                        console.log(
                            '✅ Name info successfully saved with complete data preservation',
                        );
                        void goForward();
                    }
                },
            });
        } catch (error) {
            console.error('❌ Failed to save name info:', error);
            // TODO: Show user-friendly error message
        }
    };

    // 6. Form validation and dirty state from cache
    const isPronunciationRequired = namePronunciationRequirement === 'REQUIRED';
    const canContinue = !!(
        contactInfo?.firstName?.trim() &&
        contactInfo?.lastName?.trim() &&
        (!isPronunciationRequired || contactInfo?.firstNamePronunciation?.trim()) &&
        (!isPronunciationRequired || contactInfo?.lastNamePronunciation?.trim())
    );

    if (loading) {
        return (
            <Box sx={{ padding: '32px 16px', textAlign: 'center' }}>
                <Text>プロフィール情報を読み込んでいます...</Text>
            </Box>
        );
    }

    return (
        <PageTransition pageKey="contact-info-name">
            <Helmet>
                <title>氏名を入力してください - Light Apply - Indeed</title>
            </Helmet>
            <Box sx={{ inlineSize: '100%' }}>
                <FormHeader step={1} total={5} title="氏名を入力してください" />

                {/* Form Fields - Reading from SmartApplyApplicationState cache */}
                <Box
                    sx={{
                        marginBlockEnd: '32px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '32px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '24px',
                            inlineSize: '100%',
                        }}
                    >
                        {/* 姓 (Surname) */}
                        <Box sx={{ inlineSize: '100%' }}>
                            <InputFormField
                                label="姓"
                                showRequiredIndicator={namePronunciationRequirement === 'REQUIRED'}
                                errorText={
                                    hasAttemptedContinue && !contactInfo?.lastName?.trim()
                                        ? '姓は必須です'
                                        : ''
                                }
                                invalid={hasAttemptedContinue && !contactInfo?.lastName?.trim()}
                                inputProps={{
                                    value: contactInfo?.lastName || '',
                                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                        handleFieldChange('lastName', e.target.value);
                                    },
                                    placeholder: '例）高橋',
                                }}
                            />
                        </Box>

                        {/* 名 (Given Name) */}
                        <Box sx={{ inlineSize: '100%' }}>
                            <InputFormField
                                label="名"
                                showRequiredIndicator
                                errorText={
                                    hasAttemptedContinue && !contactInfo?.firstName?.trim()
                                        ? '名は必須です'
                                        : ''
                                }
                                invalid={hasAttemptedContinue && !contactInfo?.firstName?.trim()}
                                inputProps={{
                                    value: contactInfo?.firstName || '',
                                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                        handleFieldChange('firstName', e.target.value);
                                    },
                                    placeholder: '例）理央',
                                }}
                            />
                        </Box>

                        {/* Show/hide name pronunciation fields based on namePronunciationRequirement */}
                        {(namePronunciationRequirement === 'REQUIRED' ||
                            namePronunciationRequirement === 'OPTIONAL') && (
                            <>
                                {/* せい (Surname Kana) */}
                                <Box sx={{ inlineSize: '100%' }}>
                                    <InputFormField
                                        label="せい"
                                        showRequiredIndicator={
                                            namePronunciationRequirement === 'REQUIRED'
                                        }
                                        errorText={
                                            hasAttemptedContinue &&
                                            namePronunciationRequirement === 'REQUIRED' &&
                                            !contactInfo?.lastNamePronunciation?.trim()
                                                ? 'せいは必須です'
                                                : ''
                                        }
                                        invalid={
                                            hasAttemptedContinue &&
                                            namePronunciationRequirement === 'REQUIRED' &&
                                            !contactInfo?.lastNamePronunciation?.trim()
                                        }
                                        inputProps={{
                                            value: contactInfo?.lastNamePronunciation || '',
                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                                handleFieldChange(
                                                    'lastNamePronunciation',
                                                    e.target.value,
                                                );
                                            },
                                            placeholder: '例）たかはし',
                                        }}
                                    />
                                </Box>

                                {/* めい (Given Name Kana) */}
                                <Box sx={{ inlineSize: '100%' }}>
                                    <InputFormField
                                        label="めい"
                                        showRequiredIndicator={
                                            namePronunciationRequirement === 'REQUIRED'
                                        }
                                        errorText={
                                            hasAttemptedContinue &&
                                            namePronunciationRequirement === 'REQUIRED' &&
                                            !contactInfo?.firstNamePronunciation?.trim()
                                                ? 'めいは必須です'
                                                : ''
                                        }
                                        invalid={
                                            hasAttemptedContinue &&
                                            namePronunciationRequirement === 'REQUIRED' &&
                                            !contactInfo?.firstNamePronunciation?.trim()
                                        }
                                        inputProps={{
                                            value: contactInfo?.firstNamePronunciation || '',
                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                                handleFieldChange(
                                                    'firstNamePronunciation',
                                                    e.target.value,
                                                );
                                            },
                                            placeholder: '例）りお',
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Continue Button - Bulk Save to Server */}
                    <Box>
                        <Button
                            variant="primary"
                            size="lg"
                            sx={{ inlineSize: '100%' }}
                            onClick={handleContinue}
                            disabled={!canContinue || saving || pageFlowLoading}
                        >
                            {saving ? '保存中...' : pageFlowLoading ? '読み込み中...' : '次へ'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default NameInputPage;
